import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import moment from 'moment';
import { toast } from 'react-toastify';
import { FaCheck, FaFileAlt, FaFileInvoiceDollar, FaLayerGroup, FaList, FaMoneyBill, FaPen, FaPlus, FaSearch, FaThumbsDown, FaThumbsUp, FaUpload, FaUserTie, FaHotel } from 'react-icons/fa';
import { ImCancelCircle } from 'react-icons/im';
import { IoLogoUsd } from 'react-icons/io';
import ErpTemplate from '../../components/ErpTemplate';
import Alert from '../../components/Alert';
import Data from '../../components/Data';
import FormButton from '../../components/FormButton';
import ReactSelect from '../../components/ReactSelect';
import UploadButton from '../../components/UploadButton';
import api from '../../services/api';
import { moeda } from '../../util/util';
import '../../css/financeiro-parcela-pagar.css';

export default function PainelCompra() {
    const token = localStorage.getItem('token');
    const empresa = localStorage.getItem('empresa');
    const perfil = localStorage.getItem('perfil');
    const [filtro, setFiltro] = useState({
        modulo: 2,
        empresa,
        buscando: false,
    });
    const [dados, setDados] = useState({});
    const [compras, setCompras] = useState([]);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);
    const controllerRef = useRef(null);
    const { state } = useLocation();
    const grupoTitulo = state ? (state.grupoTitulo ? state.grupoTitulo : null) : null;
    const params = new URLSearchParams(window.location.search);
    const idCompra = params.get('idCompra');

    const perfisDirecao = ['admin', 'diretor', 'gerenteprojeto', 'gerentefinanceiro'];
    const perfisFaturamento = ['admin', 'diretor', 'gerenteprojeto', 'gerentefinanceiro', 'financeiro', 'financeiro.credito', 'financeiro.adiantamento', 'gerenteevento', 'gerenteeventointernacional', 'evento'];

    const buscar = () => {
        if (controllerRef.current) controllerRef.current.abort();
        controllerRef.current = new AbortController();
        setFiltro((state) => ({ ...state, buscando: true }));
        const despesasInternas = ['admin', 'diretor', 'gerenteprojeto', 'gerentefinanceiro', 'financeiro', 'financeiro.credito', 'financeiro.adiantamento'].includes(perfil)
            ? { despesasInternas: 'sim' }
            : null;
        api({
            method: 'get',
            action: '/venda',
            token,
            params: {
                ...filtro,
                grupoTitulo,
                ...despesasInternas,
                id: idCompra,
            },
            signal: controllerRef.current.signal
        })
            .then((response) => {
                setCompras(response.data.data ?? []);
                setSelectedIds(new Set());
                setPage(1);
                setFiltro((state) => ({ ...state, buscando: false }));
            })
            .catch((err) => {
                if (err.code === "ERR_CANCELED" || err.name === "CanceledError") return;
                setFiltro((state) => ({ ...state, buscando: false }));
            });
    };

    useEffect(() => {
        buscar();
        return () => { if (controllerRef.current) controllerRef.current.abort(); };
        // eslint-disable-next-line
    }, []);

    const selectedRows = compras.filter((row) => selectedIds.has(row.id));
    const paginados = useMemo(() => {
        const inicio = (page - 1) * pageSize;
        return compras.slice(inicio, inicio + pageSize);
    }, [compras, page, pageSize]);
    const totalPaginas = Math.max(1, Math.ceil(compras.length / pageSize));
    const allSelected = paginados.length > 0 && paginados.every((row) => selectedIds.has(row.id));

    const toggleSelect = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const toggleAll = () => {
        if (allSelected) {
            setSelectedIds(new Set());
            return;
        }
        setSelectedIds(new Set(paginados.map((row) => row.id)));
    };

    const atualizarAprovacao = (aprovacao) => {
        if (selectedRows.length === 0) {
            return;
        }
        if (aprovacao === 'reprovado') {
            Alert({
                icon: 'question',
                confirmButton: 'Sair',
                denyButton: <><FaThumbsDown size={16} /> Reprovar</>,
                title: 'Reprovar',
                html: (
                    <input
                        type="text"
                        className="fin-pp__input"
                        style={{ paddingLeft: 14 }}
                        id="motivoReprovado"
                        placeholder="Motivo"
                        value={dados.motivoReprovado ?? ''}
                        onChange={(e) => setDados({ ...dados, motivoReprovado: e.target.value })}
                    />
                ),
                callBack: (result) => {
                    if (!result.isDenied) {
                        return;
                    }
                    api({
                        method: 'put',
                        action: '/venda/aprovacao',
                        token,
                        data: {
                            vendas: selectedRows.map((row) => row.id),
                            aprovacao,
                            motivoReprovado: dados.motivoReprovado,
                        }
                    }).then(() => buscar()).catch((error) => {
                        toast.error('Erro: ' + (error.response?.data?.error ?? ' ao carregar dados'));
                    });
                }
            });
            return;
        }
        Alert({
            icon: 'question',
            confirmButton: <><FaThumbsUp size={16} /> Aprovar</>,
            denyButton: 'Sair',
            title: 'Aprovar',
            callBack: (result) => {
                if (!result.isConfirmed) {
                    return;
                }
                api({
                    method: 'put',
                    action: '/venda/aprovacao',
                    token,
                    data: {
                        vendas: selectedRows.map((row) => row.id),
                        aprovacao,
                    }
                }).then(() => buscar()).catch(() => { });
            }
        });
    };

    const excluir = () => {
        if (selectedRows.length !== 1) {
            return;
        }
        Alert({
            icon: 'error',
            confirmButton: 'Sair',
            denyButton: <><ImCancelCircle size={16} /> Excluir</>,
            title: 'Excluir',
            callBack: (result) => {
                if (!result.isDenied) {
                    return;
                }
                api({
                    method: 'delete',
                    action: `/venda/${selectedRows[0].id}`,
                    token
                }).then(() => buscar()).catch(() => { });
            }
        });
    };

    const faturar = () => {
        if (selectedRows.length !== 1) {
            return;
        }
        Alert({
            icon: 'question',
            confirmButton: <><FaThumbsUp size={16} /> Faturar</>,
            denyButton: 'Sair',
            title: 'Faturar',
            callBack: (result) => {
                if (!result.isConfirmed) {
                    return;
                }
                api({
                    method: 'put',
                    action: `/venda/faturamento/${selectedRows[0].id}`,
                    token,
                    data: { tipo: '1' }
                }).then(() => buscar()).catch((error) => {
                    toast.error('Erro: ' + (error.response?.data?.error ?? ' ao carregar dados'));
                });
            }
        });
    };

    const renderPagamento = (row) => {
        const valorTotalTitulos = (row.titulo ?? []).reduce((total, titulo) => total + parseFloat(titulo.valorTotal || 0), 0);
        return (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {(row.titulo ?? []).length > 0 ? (
                    <Link
                        to={`/financeiroParcelaPagar?grupoTitulo=${row.grupoTitulo}`}
                        className="fin-pp__rowbtn"
                        title="Pagamentos registrados"
                        style={{ color: valorTotalTitulos < row.valorTotal ? 'var(--fin-wn)' : 'var(--fin-ok)' }}
                    >
                        <FaMoneyBill size={14} />
                    </Link>
                ) : null}
                <Link
                    to={{
                        pathname: '/cadastroParcelaPagar',
                        state: {
                            vendas: [row.id],
                            acao: 'insert'
                        }
                    }}
                    className="fin-pp__rowbtn"
                    title="Registrar pagamento"
                >
                    <FaPlus size={14} />
                </Link>
            </div>
        );
    };

    const renderValor = (row) => {
        const somar = (campo) => (row.vendaitem ?? []).reduce((total, item) => total + parseFloat(item[campo] || 0), 0);
        const totalicms = somar('icmsvalor');
        const totalpis = somar('pisvalor');
        const totalcofins = somar('cofinsvalor');
        const totalipi = somar('ipivalor');
        const totaliss = somar('issvalor');
        const totalirrf = somar('irrfvalor');
        const totalinss = somar('inssvalor');
        const totalcsll = somar('csllvalor');
        const totalImpostos = totalicms + totalpis + totalcofins + totalipi + totaliss + totalirrf + totalinss + totalcsll;
        const valorTotal = parseFloat(row.valorTotal || 0);
        const totalLiquido = valorTotal - totalImpostos;
        return (
            <div className="fin-pp__cell-muted" style={{ lineHeight: 1.45 }}>
                {totalicms > 0 ? <div>ICMS: {moeda(totalicms)}</div> : null}
                {totalpis > 0 ? <div>PIS: {moeda(totalpis)}</div> : null}
                {totalcofins > 0 ? <div>COFINS: {moeda(totalcofins)}</div> : null}
                {totalipi > 0 ? <div>IPI: {moeda(totalipi)}</div> : null}
                {totaliss > 0 ? <div>ISS: {moeda(totaliss)}</div> : null}
                {totalirrf > 0 ? <div>IRRF: {moeda(totalirrf)}</div> : null}
                {totalinss > 0 ? <div>INSS: {moeda(totalinss)}</div> : null}
                {totalcsll > 0 ? <div>CSLL: {moeda(totalcsll)}</div> : null}
                <div>Líquido: {moeda(totalLiquido)}</div>
                <div><strong>Total: {moeda(valorTotal)}</strong></div>
                {row.vendacontrapartida ? (
                    <Link title="Visualizar valor cobrado (Saving/Fee)" to={`/venda?idVenda=${row.vendacontrapartida.id}`}>
                        <span className="fin-pp__minitag is-ok">Cobrado: {row.vendacontrapartida.valorTotal}</span>
                    </Link>
                ) : row.totalcobrado > 0 ? (
                    <Link
                        title="Registrar valor cobrado (Saving/Fee)"
                        to={{
                            pathname: '/cadastroVenda',
                            state: {
                                vendacontrapartida: row.id,
                            }
                        }}
                    >
                        <span className="fin-pp__minitag is-warn">Cobrado: {row.totalcobrado}</span>
                    </Link>
                ) : null}
            </div>
        );
    };

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">
                <div className="fin-pp__head">
                    <div>
                        <h1 className="fin-pp__title">Faturas de Fornecedor</h1>
                        <p className="fin-pp__subtitle">Painel de requisições e faturas do hub de fornecedores.</p>
                    </div>
                    <div className="fin-pp__actions">
                        <Link to="/cadastroCompra" className="fin-pp__btn fin-pp__btn--primary fin-pp__btn--pill">
                            <FaPlus size={14} /> Nova Prestação
                        </Link>
                        <Link to="/fornecedor" className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--sm">
                            <FaUserTie size={14} /> Fornecedores
                        </Link>
                        <Link to="/hotel" className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--sm">
                            <FaHotel size={14} /> Hotéis
                        </Link>
                        <Link to="/freelancer" className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--sm">
                            <FaUserTie size={14} /> Freelancers
                        </Link>
                        <Link to="/fornecedorGrupo" className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--sm">
                            <FaLayerGroup size={14} /> Grupos
                        </Link>
                        <Link to="/participanteCategoria" className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--sm">
                            <FaList size={14} /> Categorias
                        </Link>
                        {['admin', 'diretor', 'gerenteprojeto', 'gerentefinanceiro', 'financeiro', 'financeiro.credito', 'financeiro.adiantamento'].includes(perfil) ? (
                            <Link to="/relatorioCompra" className="fin-pp__btn fin-pp__btn--pill fin-pp__btn--sm">
                                <FaFileAlt size={14} /> Relatório
                            </Link>
                        ) : null}
                    </div>
                </div>

                <form className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }} onSubmit={(e) => { buscar(); e.preventDefault(); }}>
                    <div className="fin-pp__filters">
                        <div className="fin-pp__field">
                            <label className="fin-pp__label">Evento</label>
                            <ReactSelect
                                value={filtro.evento ? { value: filtro.evento, label: filtro.nomeEvento } : null}
                                onChange={(e) => setFiltro({ ...filtro, evento: e?.value ?? null, nomeEvento: e?.label ?? '' })}
                                loadOptions={(input) => api({
                                    method: 'get',
                                    action: '/evento',
                                    token,
                                    params: { nome: input, situacao: ['ativo', 'fechado'] }
                                }).then((response) => (response.data.data ?? []).map((registro) => {
                                    let label = registro.nome;
                                    if (registro.identificacao) {
                                        label = `${registro.identificacao} - ${label}`;
                                    }
                                    if (registro.cliente) {
                                        label = `${label} - ${registro.cliente.nome}`;
                                    }
                                    return {
                                        value: registro.id,
                                        label: `${registro.codigo} - ${label}`,
                                    };
                                })).catch(() => [])}
                            />
                        </div>
                        <div className="fin-pp__field">
                            <label className="fin-pp__label">Número</label>
                            <input
                                className="fin-pp__input"
                                style={{ paddingLeft: 14 }}
                                type="text"
                                placeholder="Buscar pelo número do serviço"
                                value={filtro.codigo ?? ''}
                                onChange={(e) => setFiltro({ ...filtro, codigo: e.target.value })}
                            />
                        </div>
                        <div className="fin-pp__field">
                            <label className="fin-pp__label">Cidade</label>
                            <ReactSelect
                                value={filtro.enderecomunicipio ? { value: filtro.enderecomunicipio, label: filtro.nomeEnderecomunicipio } : null}
                                onChange={(e) => setFiltro({ ...filtro, enderecomunicipio: e?.value ?? null, nomeEnderecomunicipio: e?.label ?? '' })}
                                loadOptions={(input) => api({
                                    method: 'get',
                                    action: '/municipio',
                                    token,
                                    params: { nome: input }
                                }).then((response) => (response.data.data ?? []).map((registro) => ({ value: registro.id, label: registro.nome }))).catch(() => [])}
                            />
                        </div>
                        <div className="fin-pp__field">
                            <label className="fin-pp__label">Serviço</label>
                            <ReactSelect
                                value={filtro.produto ? { value: filtro.produto, label: filtro.nomeProduto } : null}
                                onChange={(e) => setFiltro({ ...filtro, produto: e?.value ?? null, nomeProduto: e?.label ?? '' })}
                                defaultOptions
                                loadOptions={(input) => api({
                                    method: 'get',
                                    action: '/produto',
                                    token,
                                    params: { nome: input }
                                }).then((response) => (response.data.data ?? []).map((registro) => ({ value: registro.id, label: registro.nome }))).catch(() => [])}
                            />
                        </div>
                        <div className="fin-pp__field">
                            <label className="fin-pp__label">Fornecedor</label>
                            <ReactSelect
                                value={filtro.cliente ? { value: filtro.cliente, label: filtro.nomeCliente } : null}
                                onChange={(e) => setFiltro({ ...filtro, cliente: e?.value ?? null, nomeCliente: e?.label ?? '' })}
                                loadOptions={(input) => api({
                                    method: 'get',
                                    action: '/participante',
                                    token,
                                    params: {
                                        nome: input,
                                        tipoCadastro: ['fornecedor', 'empresa'],
                                        situacaoRegistro: 'ativo',
                                    }
                                }).then((response) => (response.data.data ?? []).map((registro) => ({
                                    value: registro.id,
                                    label: `${registro.codigo} - ${registro.nome}${registro.enderecomunicipio ? ` - ${registro.enderecomunicipio.nome}-${registro.enderecomunicipio.uf}` : ''}${registro.cnpj ? ` (${registro.cnpj})` : ''}${registro.cpf ? ` (${registro.cpf})` : ''}${registro.nomefantasia ? ` - ${registro.nomefantasia}` : ''}`,
                                }))).catch(() => [])}
                            />
                        </div>
                        <div className="fin-pp__field">
                            <label className="fin-pp__label">Tipo</label>
                            <ReactSelect
                                value={filtro.tipo ? { value: filtro.tipo, label: filtro.nomeTipo } : null}
                                onChange={(e) => setFiltro({ ...filtro, tipo: e?.value ?? null, nomeTipo: e?.label ?? '' })}
                                options={[
                                    { value: '1', label: 'Fatura' },
                                    { value: '2', label: 'Requisição' },
                                ]}
                            />
                        </div>
                        <div className="fin-pp__field">
                            <label className="fin-pp__label">Responsável pelo RSVP</label>
                            <ReactSelect
                                value={filtro.coordenadorrsvp ? { value: filtro.coordenadorrsvp, label: filtro.nomeCoordenadorrsvp } : null}
                                onChange={(e) => setFiltro({ ...filtro, coordenadorrsvp: e?.value ?? null, nomeCoordenadorrsvp: e?.label ?? '' })}
                                loadOptions={(input) => api({
                                    method: 'get',
                                    action: '/participante',
                                    token,
                                    params: {
                                        nome: input,
                                        tipoCadastro: 'pessoa',
                                        situacaoRegistro: 'ativo',
                                    }
                                }).then((response) => (response.data.data ?? []).map((registro) => ({
                                    value: registro.id,
                                    label: `${registro.codigo} - ${registro.nome}${registro.enderecomunicipio ? ` - ${registro.enderecomunicipio.nome}-${registro.enderecomunicipio.uf}` : ''}${registro.cnpj ? ` (${registro.cnpj})` : ''}${registro.cpf ? ` (${registro.cpf})` : ''}${registro.nomefantasia ? ` - ${registro.nomefantasia}` : ''}`,
                                }))).catch(() => [])}
                            />
                        </div>
                        <div className="fin-pp__field">
                            <label className="fin-pp__label">Aprovação</label>
                            <ReactSelect
                                value={filtro.aprovacao ? { value: filtro.aprovacao, label: filtro.nomeAprovacao } : null}
                                onChange={(e) => setFiltro({ ...filtro, aprovacao: e?.value ?? null, nomeAprovacao: e?.label ?? '' })}
                                options={[
                                    { value: 'aprovado', label: 'Aprovado' },
                                    { value: 'reprovado', label: 'Reprovado' },
                                ]}
                            />
                        </div>
                        <Data
                            label="Data emissão"
                            value={filtro.data1}
                            onChange={(date) => setFiltro({ ...filtro, data1: date })}
                        />
                        <Data
                            label="Até a data de emissão"
                            value={filtro.data2}
                            onChange={(date) => setFiltro({ ...filtro, data2: date })}
                        />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                        <button type="submit" className="fin-pp__btn fin-pp__btn--primary" disabled={filtro.buscando}>
                            <FaSearch size={14} /> {filtro.buscando ? 'Buscando...' : 'Buscar'}
                        </button>
                    </div>
                </form>

                {selectedRows.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8, justifyContent: 'flex-end' }}>
                        {perfisDirecao.includes(perfil) ? (
                            <button type="button" className="fin-pp__btn fin-pp__btn--sm fin-pp__btn--primary" onClick={() => atualizarAprovacao('aprovado')}>
                                <FaThumbsUp size={14} /> Aprovar
                            </button>
                        ) : null}
                        {perfisDirecao.includes(perfil) ? (
                            <button type="button" className="fin-pp__btn fin-pp__btn--sm" onClick={() => atualizarAprovacao('reprovado')}>
                                <FaThumbsDown size={14} /> Reprovar
                            </button>
                        ) : null}
                        {selectedRows.length === 1 && !((selectedRows[0].aprovacao === 'aprovado') && !perfisDirecao.includes(perfil)) ? (
                            <button type="button" className="fin-pp__btn fin-pp__btn--sm fin-pp__btn--danger" onClick={excluir}>
                                <ImCancelCircle size={14} /> Excluir
                            </button>
                        ) : null}
                        {selectedRows.length === 1 ? (
                            <UploadButton
                                small={true}
                                icon={<FaUpload size={16} />}
                                color="primary"
                                acaoApi={`vendaArquivo/${selectedRows[0].id}`}
                                title="Nota Fiscal"
                                callBack={() => { buscar(); }}
                                dados={{
                                    tipoArquivo: 'notafiscal',
                                    modulo: 'compra',
                                    pasta: 'notafiscal'
                                }}
                            >
                                Nota Fiscal
                            </UploadButton>
                        ) : null}
                        {selectedRows.length > 1 ? (
                            <Link
                                to={{
                                    pathname: '/cadastroParcelaPagar',
                                    state: {
                                        vendas: selectedRows.map((row) => row.id),
                                        acao: 'agrupar'
                                    }
                                }}
                                className="fin-pp__btn fin-pp__btn--sm"
                            >
                                <IoLogoUsd size={14} /> Agrupar Pagamentos
                            </Link>
                        ) : null}
                        {selectedRows.length === 1 && perfisFaturamento.includes(perfil) ? (
                            <button type="button" className="fin-pp__btn fin-pp__btn--sm fin-pp__btn--primary" onClick={faturar}>
                                <FaCheck size={14} /> Faturar
                            </button>
                        ) : null}
                    </div>
                ) : null}

                <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                    <div className="fin-pp__tblwrap">
                        <table className="fin-pp__tbl" style={{ minWidth: 1450, tableLayout: 'auto' }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 44 }}>
                                        <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                                    </th>
                                    <th style={{ width: 58 }}></th>
                                    <th style={{ width: 58 }}></th>
                                    <th style={{ width: 120 }}>Tipo</th>
                                    <th style={{ width: 110 }}>Número</th>
                                    <th>Evento</th>
                                    <th>Fornecedor</th>
                                    <th style={{ width: 110 }}>Data</th>
                                    <th style={{ width: 120 }}>Usuário</th>
                                    <th style={{ width: 110 }}>Pagamento</th>
                                    <th style={{ width: 220 }}>Valor</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtro.buscando ? (
                                    <tr><td colSpan={11} className="fin-pp__empty"><span className="fin-pp__loading"><span className="fin-pp__spin"></span>Buscando...</span></td></tr>
                                ) : paginados.length === 0 ? (
                                    <tr><td colSpan={11} className="fin-pp__empty">Nenhum registro</td></tr>
                                ) : paginados.map((row) => (
                                    <tr key={row.id}>
                                        <td>
                                            <input type="checkbox" checked={selectedIds.has(row.id)} onChange={() => toggleSelect(row.id)} />
                                        </td>
                                        <td>
                                            {row.aprovacao === 'aprovado' && !perfisDirecao.includes(perfil) ? null : (
                                                <Link to={`/cadastroCompra/${row.id}`} className="fin-pp__rowbtn" title="Alterar fatura">
                                                    <FaPen size={14} />
                                                </Link>
                                            )}
                                        </td>
                                        <td>
                                            {row.arquivonotafiscal ? (
                                                <FormButton
                                                    method="get"
                                                    title="Nota Fiscal"
                                                    action={`${process.env.REACT_APP_API_URL}/vendaArquivo/${row.id}`}
                                                    icon={<FaFileAlt size={16} />}
                                                    color="primary"
                                                    data={{
                                                        token: localStorage.getItem('token'),
                                                        tipoArquivo: 'notafiscal'
                                                    }}
                                                />
                                            ) : null}
                                        </td>
                                        <td>
                                            <div className="fin-pp__cell-muted">
                                                <div>{row.tipo === '1' ? 'Fatura' : 'Requisição'}</div>
                                                {row.aprovacao === 'aprovado' ? <span className="fin-pp__minitag is-ok">Aprovada</span> : null}
                                                {row.aprovacao === 'reprovado' ? <span className="fin-pp__minitag is-warn" title={row.motivoReprovado ? `Motivo: ${row.motivoReprovado}` : ''}>Reprovada</span> : null}
                                            </div>
                                        </td>
                                        <td className="fin-pp__cell-muted">{row.codigo ?? ''}</td>
                                        <td className="fin-pp__cell-strong">{row.evento ? `${row.evento.codigo} - ${row.evento.nome}` : ''}</td>
                                        <td className="fin-pp__cell-muted">
                                            {row.cliente ? `${row.cliente.codigo} - ${row.cliente.nome}${row.cliente.enderecomunicipio ? ` - ${row.cliente.enderecomunicipio.nome} - ${row.cliente.enderecomunicipio.uf}` : ''}` : ''}
                                        </td>
                                        <td className="fin-pp__cell-muted">{row.data ? moment(row.data).format('DD/MM/YYYY') : ''}</td>
                                        <td className="fin-pp__cell-muted">{row.operador ? row.operador.nome : ''}</td>
                                        <td>{renderPagamento(row)}</td>
                                        <td>{renderValor(row)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="fin-pp__pagination">
                        <div className="fin-pp__pagination-info">{compras.length} registros</div>
                        <div className="fin-pp__pagination-ctrls">
                            <select
                                className="fin-pp__select"
                                style={{ height: 34 }}
                                value={pageSize}
                                onChange={(e) => {
                                    setPageSize(Number(e.target.value));
                                    setPage(1);
                                }}
                            >
                                <option value={15}>15 / pág</option>
                                <option value={30}>30 / pág</option>
                                <option value={50}>50 / pág</option>
                            </select>
                            <button type="button" className="fin-pp__btn fin-pp__btn--sm" disabled={page <= 1} onClick={() => setPage((state) => state - 1)}>
                                Anterior
                            </button>
                            <span className="fin-pp__pagination-info">{page} / {totalPaginas}</span>
                            <button type="button" className="fin-pp__btn fin-pp__btn--sm" disabled={page >= totalPaginas} onClick={() => setPage((state) => state + 1)}>
                                Próxima
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </ErpTemplate>
    );
}
